import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../AboutLocation.module.scss';
import { ESRI_FIELD_TYPES, LAYER_INFO_HIDDEN_KEYS, LAYER_ICON_MAP } from '../../../../lib/constants';
import { stringToHash } from '../../../../lib/utils';
import { generateFieldsMap, formatFieldValue } from '../utils';

import LayersIcon from '../../../../assets/svg/LayersIcon';
import AccessIcon from '../../../../assets/svg/LayerIcons/AccessIcon';
import GovernmentLandIcon from '../../../../assets/svg/LayerIcons/GovernmentLandIcon';
import HuntUnitsIcon from '../../../../assets/svg/LayerIcons/HuntUnitsIcon';
import PrivateLandIcon from '../../../../assets/svg/LayerIcons/PrivateLandIcon';
import LandUsageIcon from '../../../../assets/svg/LayerIcons/LandUsageIcon';
import TerrainIcon from '../../../../assets/svg/LayerIcons/TerrainIcon';
import WaterIcon from '../../../../assets/svg/LayerIcons/WaterIcon';
import WildfireIcon from '../../../../assets/svg/LayerIcons/WildfireIcon';
import RoadsIcon from '../../../../assets/svg/LayerIcons/RoadsIcon';
import SpeciesIcon from '../../../../assets/svg/LayerIcons/SpeciesIcon';
import ChevronDown from '../../../../assets/svg/Feather/ChevronDown';

const SPECIES_DISTRIBUTION_CATEGORY = 'Species Distribution';

// ── Layer icon resolution ─────────────────────────────────────────────────────

const LAYER_ICON_COMPONENTS = {
  'AccessIcon': AccessIcon,
  'GovernmentLandIcon': GovernmentLandIcon,
  'HuntUnitsIcon': HuntUnitsIcon,
  'PrivateLandIcon': PrivateLandIcon,
  'LandUsageIcon': LandUsageIcon,
  'TerrainIcon': TerrainIcon,
  'WaterIcon': WaterIcon,
  'WildfireIcon': WildfireIcon,
  'RoadsIcon': RoadsIcon,
  'SpeciesIcon': SpeciesIcon,
};

function getLayerIcon(category, color = '#ffffff') {
  if (!category) return <LayersIcon color={color} style={{ width: 20, height: 20, flexShrink: 0 }} />;
  const key = category.toLowerCase();
  const iconName = LAYER_ICON_MAP[key];
  if (iconName) {
    const IconComponent = LAYER_ICON_COMPONENTS[iconName];
    if (IconComponent) return <IconComponent color={color} />;
  }
  for (const [mapKey, name] of Object.entries(LAYER_ICON_MAP)) {
    if (key.includes(mapKey) || mapKey.includes(key)) {
      const IconComponent = LAYER_ICON_COMPONENTS[name];
      if (IconComponent) return <IconComponent color={color} />;
    }
  }
  return <LayersIcon color={color} style={{ width: 20, height: 20, flexShrink: 0 }} />;
}

function ActiveLayerSection({ layerAtLocation }) {
  const { layer, metadata } = layerAtLocation;
  const [isExpanded, setIsExpanded] = useState(layer.category !== SPECIES_DISTRIBUTION_CATEGORY);
  const urlMatcher = /((http|https):\/\/)/gi;

  const seen = new Set();
  const filteredFeatures = [];
  for (let fi = 0; fi < metadata.features.length; fi++) {
    const feature = metadata.features[fi];
    const key = feature.attributes.objectid ?? JSON.stringify(feature.attributes);
    if (!seen.has(key)) { seen.add(key); filteredFeatures.push(feature); }
  }

  const fields = generateFieldsMap(metadata.fields);
  const sectionTitle = layer.subCategory != null && layer.subCategory !== layer.category
    ? `${layer.category} / ${layer.subCategory}`
    : layer.category;

  return (
    <div className={styles.activeLayerSection}>
      <div className={styles.activeLayerSectionHeader} onClick={() => setIsExpanded(v => !v)}>
        {getLayerIcon(layer.category)}
        <span className={styles.activeLayerSectionTitle}>{sectionTitle}</span>
        <span className={`${styles.activeLayerChevron} ${isExpanded ? styles.activeLayerChevronOpen : ''}`}>
          <ChevronDown width={18} color='#B5B5B8' />
        </span>
      </div>

      {isExpanded && (
        <div className={styles.activeLayerSectionBody}>
          {filteredFeatures.map((feature, j) => {
            const visibleFields = Object.keys(feature.attributes).filter(attrName => {
              if (!feature.attributes[attrName] || feature.attributes[attrName].toString().trim() === '') return false;
              const keyAlias = fields[attrName]?.alias?.trim();
              const keyType = fields[attrName]?.type;
              if (!keyAlias) return false;
              if (LAYER_INFO_HIDDEN_KEYS.includes(keyAlias.toLowerCase())) return false;
              if (keyType === ESRI_FIELD_TYPES.GlobalID || keyType === ESRI_FIELD_TYPES.ObjectID) return false;
              return true;
            });

            return (
              <div key={`${j}-${stringToHash(JSON.stringify(feature))}`} className={styles.activeLayerFeature}>
                {visibleFields.map((attrName, i) => {
                  const keyType = fields[attrName].type;
                  const keyAlias = fields[attrName].alias?.trim();
                  const content = formatFieldValue(feature.attributes[attrName], keyType, keyAlias);
                  const isFirst = i === 0;

                  return (
                    <div key={i} className={isFirst ? styles.activeLayerFeatureName : styles.activeLayerFeatureField}>
                      {keyType === ESRI_FIELD_TYPES.String && urlMatcher.test(content) ? (
                        <a target="_blank" href={content} rel="noreferrer">{content.toString().trim()}</a>
                      ) : (
                        content.toString().trim()
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

ActiveLayerSection.propTypes = {
  layerAtLocation: PropTypes.object,
};

export default ActiveLayerSection;
